import React from 'react';
import {
  Group,
  type GroupImperativeHandle,
  useDefaultLayout,
} from 'react-resizable-panels';

// v2 parity: hitAreaMargins {coarse: 15, fine: 5} around a 1px handle gave a
// total hit target of 31px (touch) / 11px (mouse); v4 expresses that as the
// minimum total size of the hit rect, configured on the group.
const RESIZE_TARGET_MINIMUM_SIZE = { coarse: 31, fine: 11 };

type LegacyLayoutRecord = Record<string, { layout?: number[] }>;

/**
 * Reads a layout saved by the pre-v4 library, which the library's own
 * migration cannot restore here: it keyed each combination by panel ids sorted
 * alphabetically while storing the sizes in panel order, so zipping the key
 * against the sizes mismatches whenever the two orders differ. Taking the
 * order from the rendered panels instead pairs them correctly.
 *
 * Safe to delete once these layouts have aged out of users' browsers.
 */
const readLegacyLayout = (
  groupId: string,
  panelIds: string[] | undefined,
): Record<string, number> | undefined => {
  if (!panelIds) return undefined;
  try {
    const stored = localStorage.getItem(`react-resizable-panels:${groupId}`);
    if (!stored) return undefined;
    const record = JSON.parse(stored) as LegacyLayoutRecord;
    const sortedKey = [...panelIds]
      .sort((a, b) => a.localeCompare(b))
      .join(',');
    const layout = record[sortedKey]?.layout;
    if (!Array.isArray(layout) || layout.length !== panelIds.length) {
      return undefined;
    }
    return Object.fromEntries(panelIds.map((id, index) => [id, layout[index]]));
  } catch {
    return undefined;
  }
};

type ResizableGroupProps = {
  children: React.ReactNode;
  widthPersistKey?: string;
  /**
   * All panel ids the group can contain. Required when panels are
   * conditionally rendered so persisted layouts (including ones saved by the
   * pre-v4 library) restore for the full panel combination.
   */
  persistPanelIds?: string[];
  className?: string;
};

const PersistedGroup = React.forwardRef<
  GroupImperativeHandle,
  ResizableGroupProps & { widthPersistKey: string }
>(({ children, widthPersistKey, persistPanelIds, className }, ref) => {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: widthPersistKey,
    panelIds: persistPanelIds,
  });
  const legacyLayout = React.useMemo(
    () => readLegacyLayout(widthPersistKey, persistPanelIds),
    [widthPersistKey, persistPanelIds],
  );
  return (
    <Group
      groupRef={ref}
      orientation="horizontal"
      resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
      defaultLayout={defaultLayout ?? legacyLayout}
      onLayoutChanged={onLayoutChanged}
      className={className}
    >
      {children}
    </Group>
  );
});
PersistedGroup.displayName = 'PersistedGroup';

// useDefaultLayout requires an id, so the persisted variant is a separate
// component rather than a conditional hook call.
export const ResizableGroup = React.forwardRef<
  GroupImperativeHandle,
  ResizableGroupProps
>((props, ref) =>
  props.widthPersistKey !== undefined ? (
    <PersistedGroup
      {...props}
      widthPersistKey={props.widthPersistKey}
      ref={ref}
    />
  ) : (
    <Group
      groupRef={ref}
      orientation="horizontal"
      resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
      className={props.className}
    >
      {props.children}
    </Group>
  ),
);
ResizableGroup.displayName = 'ResizableGroup';
